// Sync has been changed, it will be done in a background process in rails
// The process is as following:
// 1. Any modification done in the react app, will change the synced status
//    'synced' of workorder to false.
// 2. A workorder that is not synced will get callbacks from useEffect to trigger
//    the redux sync action.
// 3. When it triggers we assume an optimistic response from the back so the
//    the redux state will change the synced and syncing attributes to true
// 4. A request will arrive to the server with the payload and a quick response
//    will be given when the background job is triggered
// 5. If the response has errors we will trigger the failure response and increase
//    the retries counter by one, also will update the synced and syncing
//    attributes to false
// 6. If the response from the web worker (not the background worker) is OK, a
//    websocket will be opened to recieve the response from the background job
// 7. Background job finishes and sends the updated workorder back to the consumer
// 8. Version is checked, if it is the same, redux state of the workorder is replaced.

import { PATHS, STATUSES }            from '@helpers/javascript/config/CONSTANTS'
import { getRequest }                 from '@helpers/javascript/javascript'
import { bodyRequest }                from '@helpers/javascript/javascript'
import { shouldAlertError }           from '@helpers/javascript/javascript'
import { cableSubscription }          from '@helpers/javascript/cable/cable'

import { store }                      from '@redux/store'
import { addNoticeAction }            from '@redux/notices/actions'
import { syncWorkorderBeginAction   } from '@redux/workorders/actions/sync'
import { syncWorkorderSuccessAction } from '@redux/workorders/actions/sync'
import { syncWorkorderFailureAction } from '@redux/workorders/actions/sync'
import { syncWorkorderRetryAction   } from '@redux/workorders/actions/sync'
import { releasePhotos }              from '@redux/workorders/actions/sync'
import { resetFiltersAction }         from '@redux/filters/filters'
import { push }                       from 'connected-react-router'
export class Synchronizer  {
  channel     = 'WorkorderSyncChannel'
  static timeout    = 6000
  static maxRetries = 4
  static active     = new Map()
  handlers    = { received: response => this._receive(response) }

  constructor(workorder) {
    this.version    = workorder.version
    this.retries    = 0
    this.workorder  = workorder
    this.identifier = {
      channel:   this.channel,
      workorder: this.workorder.id
    }
    this.subscription = cableSubscription(this.identifier, this.handlers)
    Synchronizer.active.set(this.workorder.id, this)
  }

  transmit = () => {
    if(this.workorder.error) return

    store.dispatch(syncWorkorderBeginAction(this.workorder))
    this._send()
  }

  reconcile = () => {
    this._checkVersion(() => {
      this.version   = new Date().getTime()
      this.workorder = { ...this.workorder, version: this.version }
      this.transmit()
    })
  }

  beginTimeout = () => {
    this.retryTimeout = setTimeout(() => {
      if(this.done) return

      const url = PATHS.workorders.version(this.workorder.id)
      getRequest(url).then(data => {
        if(this.version == data.version) {
          // For some reason, the websocket response did not arrived
          this._success(data.version)
        } else {
          this._retry()
        }
      })
      .catch(() => this._retry())
    }, Synchronizer.timeout)
  }

  // Private methods
  _send = () => {
    const method = 'PATCH'
    const url    = PATHS.workorders.sync(this.workorder.id)
    const body   = { workorder: stripWorkorder(this.workorder) }

    bodyRequest({ method, url, body })
    .then(response => {
      const { failure, message } = response
      failure ? this._fail(message, { fromServer: true }) : this.beginTimeout()
    })
    .catch(error => isNetworkError(error) ? this._resend() : this._fail(error.message, { fromServer: true }))
  }

  // A PATCH cut by the network may still have landed: check the version, then re-send.
  // The server runs syncs one at a time and dedupes photos, so a double is harmless.
  _resend = () => {
    if(this.done) return
    this.retries += 1
    if(this.retries > Synchronizer.maxRetries) return this._fail("Timeout")

    store.dispatch(syncWorkorderRetryAction(this.workorder))
    this._log('Resend')

    this.retryTimeout = setTimeout(() => {
      if(this.done) return
      this._checkVersion(() => this._send())
    }, Synchronizer.timeout)
  }

  _checkVersion = send => {
    getRequest(PATHS.workorders.version(this.workorder.id))
    .then(data => {
      if(this.done) return
      if(this.version == data.version) return this._success(data.version)

      // An edit landed during the version check: abandon this stale re-send so the
      // driver syncs the fresh content, otherwise the edit is masked and lost.
      const current = store.getState().workorders.items.find(wo => wo.id == this.workorder.id)
      if(current && current.synced === false) {
        this.done = true
        this.subscription.unsubscribe()
        this._release()
        this._resync()
        return
      }

      send()
    })
    .catch(() => this._resend())
  }

  _receive = data => {
    // It comes as a string in dev, as an object in prod
    const response = typeof(data) == 'string' ? JSON.parse(data) : data

    const { failure, message, version, sent } = response
    if(this.done) return this._log(`Dropped ${version}`)
    // A duplicate job of an earlier send echoes its version: the version poll still runs.
    if(!failure && version != this.version) return this._log(`Ignored ${version}`)
    // A late failure of an abandoned earlier send is not this send's
    if(failure && sent && sent != this.version) return this._log(`Ignored failure of ${sent}`)

    response.failure ? this._fail(message, { fromServer: true }) : this._success(version, message)
  }

  _retry = () => {
    if(this.done) return
    this.retries += 1
    if(this.retries > Synchronizer.maxRetries) {
      this._fail("Timeout")
    } else {
      store.dispatch(syncWorkorderRetryAction(this.workorder))
      this.beginTimeout()
      this._log('Retry')
    }
  }

  _success = (version, message) => {
    if(this.done) return
    this.done = true
    this.retryTimeout && clearTimeout(this.retryTimeout)

    if(this._drop(version)) return

    // Get the server data
    getRequest(PATHS.workorders.fetch(this.workorder.id))
    .then(response => {
      const workorder = response.workorder
      if(this._drop(workorder.version)) return

      this.subscription.unsubscribe()
      this._release()
      this._log('Success')
      store.dispatch(syncWorkorderSuccessAction(workorder))
      if(message) store.dispatch(addNoticeAction({ message, type: 'notice' }))

      // Unconfirmed photos are sent again: keep the technician on the workorder.
      const synced = store.getState().workorders.items.find(wo => wo.id == workorder.id)
      if(synced && synced.error) {
        store.dispatch(addNoticeAction({ message: synced.error, type: 'notice' }))
        return
      }
      if(synced && synced.synced === false) return this._resync()
      if(workorder.authorize_invoice) return

      if(workorder.status == STATUSES.CLOSED) {
        const state = store.getState()
        const pathname = state.router.location.pathname

        if (pathname == PATHS.workorders.show(workorder.id)) {
          const referrer = state.router.location.state?.referrer
          store.dispatch(resetFiltersAction())
          if (state.account.portal_active) {
            const path = referrer?.demand_filter ? PATHS.demands.index + referrer.demand_filter : PATHS.demands.index
            store.dispatch(push(path))
          } else if (this.workorder.should_redirect_on_sync) {
            store.dispatch(push(PATHS.workorders.index))
          }
        }
      }
    })
    .catch(() => {
      this.subscription.unsubscribe()
      this._release()
    })
  }

  _drop = version => {
    const workorders = store.getState().workorders.items
    const workorder  = workorders.find(wo => wo.id == this.workorder.id)

    if(workorder) {
      // The analized version should be equal to the redux version
      // and the synchronizer version
      // The workorder also must be unlocked
      // The workorder must not have pending local changes (synced !== false)
      // to avoid overwriting edits made during an in-flight sync

      const unlocked       = !workorder.locked
      const noLocalChanges = workorder.synced !== false
      const sVersion       = version == this.version
      const rVersion       = version == workorder.version

      if(unlocked && noLocalChanges && sVersion && rVersion) return false
    }

    this.subscription.unsubscribe()
    this._release()
    this._log(`Dropped ${version}`)
    this._resync()
    return true
  }

  // The page's sync request was refused while this one ran: send the newer edit now
  _resync = () => {
    const current = store.getState().workorders.items.find(wo => wo.id == this.workorder.id)
    if(!current || current.synced !== false || current.error) return
    if(!store.getState().offline.online) return
    if(Synchronizer.active.has(current.id)) return

    new Synchronizer({ ...current, version: new Date().getTime() }).transmit()
  }

  _fail = (error, { fromServer = false } = {}) => {
    if(this.done) return
    this.done = true
    this.retryTimeout && clearTimeout(this.retryTimeout)

    // Connectivity loss is not a sync failure: keep the in-flight state so the next
    // driver reconciles it. A server-reported failure is real regardless of the flag.
    if(!fromServer && !store.getState().offline.online) {
      this.subscription.unsubscribe()
      this._release()
      this._log('Deferred')
      return
    }

    const reason = error || I18n.t('mobile.sync.not_synced')

    // Deliberately not `reason`: its fallback only repeats the tag the card already shows.
    if(shouldAlertError(reason)) {
      store.dispatch(addNoticeAction({
        message: error || I18n.t('mobile.sync.unsynced_hint'),
        type:    'notice'
      }))
    }
    store.dispatch(syncWorkorderFailureAction(this.workorder, reason))
    this.subscription.unsubscribe()
    this._release()
    this._log('Failure')
  }

  _release = () => {
    if(Synchronizer.active.get(this.workorder.id) === this) {
      Synchronizer.active.delete(this.workorder.id)
    }
  }

  _log = message => console.info(this.channel, this.version, message)
}

const syncKeys = [
  "id",
  "status",
  "departure_date",
  "date_planned",
  "date_started",
  "date_done",
  "paused",
  "paused_time",
  "start_pause",
  "manual_travel_time",
  "report",
  "time_taken",
  "signature",
  "signer",
  "version",
  "extra_field_answers",
  "blueprint_answers",
  "maintenance_tasks",
  "readings",
  "material_placements",
  "proximity_start",
  "proximity_stop",
  "proximity_start_status",
  "proximity_stop_status"
]

// The server skips a photo still flagged syncing by a cut sync.
const stripWorkorder = workorder => {
  const stripped = {}
  syncKeys.forEach(key => stripped[key] = workorder[key])
  stripped.photos = releasePhotos(workorder.photos)
  return stripped
}

// No response at all ("Load failed" on Safari), or a gateway page instead of JSON.
const isNetworkError = error =>
  error instanceof TypeError || error instanceof SyntaxError ||
  /Load failed|Failed to fetch|NetworkError/.test(error?.message || '')
